import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const VerticalScrollViewWrapper = styled.div`
  overflow: hidden;
  flex: 1;
  position: relative;
`;

const ScrollContentWrapper = styled.div`
  ${({ scrollingEnabled }) => css`
    margin-bottom: ${scrollingEnabled ? 'inherit' : '0'};
    width: 100%;
    height: 100%;
    overflow: hidden;
    margin-right: -30px;
  `};
`;

const ScrollContent = styled.div`
  ${({ scrollingEnabled }) => css`
    overflow-x: hidden;
    overflow-y: ${scrollingEnabled ? 'scroll' : 'hidden'};
    width: calc(100% + 30px);
    min-height: 0;
    max-height: 194px;
    height: ${scrollingEnabled ? '194px' : 'auto'};
    padding-right: 13px;
    padding-bottom: ${scrollingEnabled ? 'inherit' : '0'};
  `};
`;

const Scrollbar = styled.div`
  ${({ theme, scrollingEnabled }) => css`
    display: ${scrollingEnabled ? 'inherit' : 'none'};
    overflow: hidden;
    margin: 0 0 0 2px;
    width: 8px;
    height: calc(100% - 8px);
    background: ${theme.colors.shades[6]};
    border-radius: 4px;
    position: absolute;
    right: 5px;
    top: 4px;
  `};

`;

const ScrollbarHandle = styled.div`
  ${({ theme }) => css`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    background: ${theme.colors.shades[4]};
    border-radius: 4px;
  `};
`;

class VerticalScrollView extends Component {
  scrollbarRef = React.createRef();

  state = {
    scrollable: true
  };

  componentDidMount() {
    this.updateHandleSize();
    window.addEventListener('resize', this.updateHandleSize);
    window.addEventListener('mousemove', this.dragHandle);
    window.addEventListener('mouseup', this.stopDraggingHandle);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateHandleSize);
    window.removeEventListener('mousemove', this.dragHandle);
    window.removeEventListener('mouseup', this.stopDraggingHandle);
  }

  getScrollHeight = () => {
    if (!this.content) {
      return 0;
    }
    let scrollHeight = 0;
    const children = this.content.children || [];
    for (const child of children) {
      if (child) {
        scrollHeight += child.clientHeight;
      }
    }
    return scrollHeight;
  }

  updateHandlePosition = () => {
    const { scrollbarCorrection } = this.props;
    const { scrollTop, clientHeight } = this.content;
    const scrollHeight = this.getScrollHeight();
    const scrollPosition = scrollTop / (scrollHeight - clientHeight);
    const handleOffset = Math.floor(
      (clientHeight - scrollbarCorrection - this.handle.clientHeight) * scrollPosition
    );
    this.handle.style.top = `${handleOffset}px`;

  };

  updateHandleSize = () => {
    if (!this.content || !this.handle) {
      return;
    }

    const { scrollbarCorrection } = this.props;
    const { clientHeight } = this.content;
    const scrollHeight = this.getScrollHeight();

    const handleSize = Math.floor(
      ((clientHeight - scrollbarCorrection) / scrollHeight) * (clientHeight - scrollbarCorrection)
    );
    this.handle.style.height = `${handleSize}px`;

    this.updateHandlePosition();

    const scrollable = scrollHeight > clientHeight;
    if (scrollable !== this.state.scrollable) {
      this.setState({ scrollable });
    }
  };

  startDraggingHandle = (mouse) => {
    this.isMouseDown = true;
    this.startMouseDownPosition = mouse.clientY;
  };

  stopDraggingHandle = () => {
    this.isMouseDown = false;
  };

  dragHandle = (mouse) => {
    if (!this.isMouseDown) return;

    const { clientHeight } = this.content;
    const scrollHeight = this.getScrollHeight();

    const mouseMove = (mouse.clientY - this.startMouseDownPosition);
    const speed = (scrollHeight - clientHeight) / (clientHeight - this.handle.clientHeight);
    this.content.scrollTop += mouseMove * speed;

    this.startMouseDownPosition = mouse.clientY;
    this.updateHandlePosition();
  };
  
  render() {
    const scrollingEnabled = this.props.scrollEnabled && this.state.scrollable;
    return (
      <VerticalScrollViewWrapper data-qa={this.props.qa}>
        <ScrollContentWrapper scrollingEnabled={scrollingEnabled}>
          <ScrollContent
            scrollingEnabled={scrollingEnabled}
            ref={(el) => { this.content = el; }}
            onScroll={this.updateHandlePosition}
          >
            {this.props.children}
          </ScrollContent>
        </ScrollContentWrapper>
        <Scrollbar scrollingEnabled={scrollingEnabled}>
          <ScrollbarHandle
            onMouseDown={this.startDraggingHandle}
            ref={(el) => { this.handle = el; }}
          />
        </Scrollbar>
      </VerticalScrollViewWrapper>
    );
  }
}

VerticalScrollView.displayName = 'VerticalScrollView';

VerticalScrollView.propTypes = {
  scrollEnabled: PropTypes.bool.isRequired,
  scrollbarCorrection: PropTypes.number,
  children: PropTypes.node.isRequired,
  qa: PropTypes.string
};

VerticalScrollView.defaultProps = {
  scrollbarCorrection: 0,
  qa: undefined
};

export default VerticalScrollView;
